import * as React from 'react'
import type {
  ColumnDescriptionEdit,
  OutputTable,
  PageTable
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import { Dialog, DialogContent, DialogFooter } from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { Textarea } from '../../../components/ui/textarea'
import { changeReasonGiven, changeReasonNote } from './changeReason'
import {
  EDITOR_BODY_CLASS,
  EDITOR_CARD_CLASS,
  EDITOR_DIALOG_CLASS,
  EDITOR_INPUT_CLASS,
  EDITOR_LABEL_CLASS,
  EditorDialogHeader,
  EditorError,
  EditorField
} from './editorChrome'

const REASON_PREFILL = 'description edited: '

export interface EditDescriptionSubmit {
  description: string
  /** Only in `revise` mode: column hints that changed. */
  columnDescriptions?: ColumnDescriptionEdit[]
  changeReason?: string
  /** Only in `revise` mode: the reason without its "description edited: " prefill. */
  note?: string
}

/**
 * The description, and in `revise` mode each column's one-line hint, so a
 * column note can be fixed without a rebuild. A script config saves the edit
 * as a new version; for an agent config the text is the extractor's prompt,
 * so the edit becomes a draft once the example pages are read with it.
 */
export function EditDescriptionDialog({
  open,
  onOpenChange,
  mode,
  parserKind = 'script',
  exampleCount = 0,
  initial,
  tables,
  pageTables = false,
  loading,
  error,
  onSubmit
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'revise' | 'overrides'
  parserKind?: 'script' | 'agent'
  /** The saved version's example files, for the agent copy. */
  exampleCount?: number
  initial: string
  /** The tables whose column hints are edited: the output tables, or an agent config's page tables. */
  tables: Array<OutputTable | PageTable>
  /** Set when `tables` are page tables that a finalize script writes the output tables from. */
  pageTables?: boolean
  loading: boolean
  error?: string
  onSubmit: (input: EditDescriptionSubmit) => void
}) {
  const drafts = mode === 'revise' && parserKind === 'agent'
  const [description, setDescription] = React.useState(initial)
  const [reason, setReason] = React.useState(REASON_PREFILL)
  const [columns, setColumns] = React.useState<Record<string, string>>({})
  // Snapshot the starting point when the dialog opens — not whenever the
  // parent re-renders with fresh `initial` / `tables` identities mid-edit.
  const startRef = React.useRef({ initial, tables })
  startRef.current = { initial, tables }
  React.useEffect(() => {
    if (open) {
      const start = startRef.current
      setDescription(start.initial)
      setReason(REASON_PREFILL)
      setColumns(
        Object.fromEntries(
          start.tables.flatMap((table) =>
            table.columns.map((column) => [
              `${table.table}.${column.name}`,
              column.description ?? ''
            ])
          )
        )
      )
    }
  }, [open])
  const changedColumns: ColumnDescriptionEdit[] = tables.flatMap((table) =>
    table.columns
      .filter(
        (column) =>
          (columns[`${table.table}.${column.name}`] ?? '').trim() !==
          (column.description ?? '').trim()
      )
      .map((column) => ({
        table: table.table,
        column: column.name,
        description: (columns[`${table.table}.${column.name}`] ?? '').trim()
      }))
  )
  const dirty = description.trim() !== initial || changedColumns.length > 0
  const reasonOk = mode === 'overrides' || changeReasonGiven(reason, REASON_PREFILL)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${EDITOR_DIALOG_CLASS} sm:max-w-3xl`}>
        <EditorDialogHeader
          title={mode === 'revise' ? 'Edit description' : 'Edit description on this draft'}
        >
          {drafts
            ? `For an agent parser this is the extractor's prompt. Saving re-reads every page of the ${exampleCount} example file${
                exampleCount === 1 ? '' : 's'
              } with the new text and parks a draft for review; nothing changes until you approve it.${
                pageTables
                  ? ' The pages are read into the page tables below, which finalize turns into the output tables; output column hints change with Edit contract.'
                  : ''
              }`
            : `What the file is and how to read it. ${
                mode === 'revise'
                  ? 'Saving creates a new version; no builder runs.'
                  : 'The edit is saved with the draft when you press Save.'
              }`}
        </EditorDialogHeader>
        <div className={EDITOR_BODY_CLASS}>
          <label className="flex flex-col gap-1">
            <span className={EDITOR_LABEL_CLASS}>Description</span>
            {/* `shrink-0`: in this flex column the field would otherwise collapse to
                its minimum when the hints below need room, hiding most of the text. */}
            <Textarea
              aria-label="Description"
              rows={8}
              className="min-h-40 shrink-0 px-2.5 py-2 text-xs leading-5 md:text-xs"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          {mode === 'revise'
            ? tables.map((table) => (
                <section key={table.table} className={`${EDITOR_CARD_CLASS} flex flex-col gap-1.5`}>
                  <p className={EDITOR_LABEL_CLASS}>
                    {pageTables ? 'Page table hints' : 'Column hints'} ·{' '}
                    <span className="font-mono font-normal text-ink-2">{table.table}</span>
                  </p>
                  <div className="grid grid-cols-[minmax(7rem,13rem)_minmax(0,1fr)] items-center gap-x-3 gap-y-1">
                    {table.columns.map((column) => {
                      const key = `${table.table}.${column.name}`
                      return (
                        <React.Fragment key={key}>
                          <span
                            className="truncate font-mono text-11 text-ink-2"
                            title={column.name}
                          >
                            {column.name}
                          </span>
                          <Input
                            aria-label={`Hint for ${key}`}
                            className={EDITOR_INPUT_CLASS}
                            placeholder="One line on what this column holds"
                            value={columns[key] ?? ''}
                            onChange={(event) =>
                              setColumns((current) => ({ ...current, [key]: event.target.value }))
                            }
                          />
                        </React.Fragment>
                      )
                    })}
                  </div>
                </section>
              ))
            : null}
        </div>
        {mode === 'revise' ? (
          <EditorField
            label="Why this change (saved with the version)"
            ariaLabel="Change reason"
            value={reason}
            onChange={setReason}
          />
        ) : null}
        <EditorError error={error} />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={loading || !description.trim() || !dirty || !reasonOk}
            onClick={() =>
              onSubmit({
                description: description.trim(),
                ...(mode === 'revise'
                  ? {
                      columnDescriptions: changedColumns,
                      changeReason: reason.trim(),
                      note: changeReasonNote(reason, REASON_PREFILL)
                    }
                  : {})
              })
            }
          >
            {drafts
              ? 'Verify and draft'
              : mode === 'revise'
                ? 'Save as new version'
                : 'Apply to draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
